import type { ReactElement } from 'react';
import {
  FieldError,
  Input,
  Label,
  Text,
  TextField as AriaTextField,
  type TextFieldProps as AriaTextFieldProps,
} from 'react-aria-components';
import { useDollarCents } from './DollarField';
import s from './fields.module.css';

export interface MoneyFieldProps extends Omit<
  AriaTextFieldProps,
  'children' | 'className' | 'value' | 'defaultValue' | 'onChange' | 'inputMode' | 'type'
> {
  label: string;
  /** Money is integer cents everywhere (APP-010). `null` means empty. */
  valueCents: number | null;
  /** The amount once it's entered (on leaving the box or pressing Enter), within the limits. */
  onChangeCents?: (cents: number | null) => void;
  minCents?: number;
  maxCents?: number;
  /** Whole dollars only (most award amounts). */
  wholeDollars?: boolean;
  description?: string;
  errorMessage?: string;
}

/**
 * A labelled US-dollar input that reads and writes integer cents. APP-253 (change 9): it formats
 * as it's typed ("$1,000"), through `useDollarCents`, in place of a React Aria NumberField.
 */
export function MoneyField({
  label,
  valueCents,
  onChangeCents,
  minCents = 0,
  maxCents,
  wholeDollars = true,
  description,
  errorMessage,
  onFocusChange,
  ...rest
}: MoneyFieldProps): ReactElement {
  const { field, inputRef } = useDollarCents({
    valueCents,
    onChangeCents,
    minCents,
    maxCents,
    wholeDollars,
    onFocusChange,
  });
  return (
    <AriaTextField {...rest} {...field} className={s.field}>
      <Label className={s.label}>{label}</Label>
      <div className={s.body}>
        <Input ref={inputRef} className={s.input} />
        {description !== undefined && (
          <Text slot="description" className={s.description}>
            {description}
          </Text>
        )}
        <FieldError className={s.error}>{errorMessage}</FieldError>
      </div>
    </AriaTextField>
  );
}
